import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMessage } from "../lib/api";
import { n } from "../lib/format";
import { resendVerification, signInPath } from "./Guards";
import { LikeBar } from "./Bits";
import { SharePopover, nativeShare } from "./SharePopover";

export function useVote(p, onChange) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ likes: p.likes, dislikes: p.dislikes, pct: p.pct, my_vote: p.my_vote ?? null });
  const [busy, setBusy] = useState(false);

  const cast = async (value) => {
    if (!user) {
      navigate(signInPath(`/proposal/${p.slug}`));
      return;
    }
    if (!user.email_verified) {
      toast.error("Confirm your email address first. We sent you a link.", { action: { label: "Send again", onClick: resendVerification } });
      return;
    }
    const next = state.my_vote === value ? null : value;
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/${p.id}/vote`, { value: next });
      setState(data);
      onChange?.(data);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { ...state, cast, busy, signedIn: !!user };
}

const TONE = { like: "#C8102E", dislike: "#14232a" };
const LABELS = { like: ["Like", "Liked"], dislike: ["Dislike", "Disliked"] };

const voteStyle = (on, kind) => ({
  background: on ? TONE[kind] : "#fff",
  color: on ? "#fff" : "#14232a",
  border: `2px solid ${on ? TONE[kind] : "#c9d6da"}`,
});

export const VoteButtons = ({ v, size = 52, compactLabels = false, prefix = "vote" }) => (
  <div role="group" aria-label="Your vote" style={{ display: "flex", gap: 10, flex: compactLabels ? "none" : 1 }}>
    {["like", "dislike"].map((kind) => {
      const on = v.my_vote === kind;
      const Icon = kind === "like" ? ThumbsUp : ThumbsDown;
      const label = LABELS[kind][on ? 1 : 0];
      return (
        <button key={kind} type="button" aria-pressed={on} disabled={v.busy} onClick={() => v.cast(kind)} data-testid={`${prefix}-${kind}-btn`}
          style={{ flex: compactLabels ? "none" : 1, minHeight: size, padding: compactLabels ? "0 14px" : 0, borderRadius: 12, cursor: "pointer",
            font: "600 16px Inter, sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "background-color .15s, color .15s, border-color .15s", ...voteStyle(on, kind) }}>
          <Icon size={18} />{label}
        </button>
      );
    })}
  </div>
);

export const share = nativeShare;

export const VoteCard = ({ p, v }) => {
  const shareP = useMemo(() => ({ ...p, likes: v.likes }), [p, v.likes]);
  return (
    <section aria-labelledby="h-vote" className="lift" data-testid="vote-card">
      <h2 id="h-vote" style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)", margin: 0 }}>What do you think?</h2>
      <p style={{ margin: "10px 0 12px", display: "flex", alignItems: "baseline", gap: 8 }}>
        <span className="head" style={{ fontSize: 44, fontWeight: 700, lineHeight: 1 }} data-testid="vote-pct">{v.pct}%</span>
        <span style={{ fontSize: 16, color: "var(--body)" }}>like this</span>
      </p>
      <LikeBar pct={v.pct} height={10} grey />
      <p style={{ fontSize: 14, color: "var(--body)", margin: "10px 0 20px", display: "flex", justifyContent: "space-between" }}>
        <span><strong data-testid="vote-likes">{n(v.likes)}</strong> likes</span>
        <span><strong data-testid="vote-dislikes">{n(v.dislikes)}</strong> dislikes</span>
      </p>
      <VoteButtons v={v} />
      <p aria-live="polite" style={{ fontSize: 13, lineHeight: 1.5, color: "var(--muted)", margin: "12px 0 18px" }}>
        {v.signedIn ? "Nobody else can see how you voted. Tap your choice again to remove it." :
          "Sign in to vote. Nobody else can see how you voted, and you can change it at any time."}
      </p>
      <SharePopover p={shareP} />
    </section>
  );
};

export const MobileVoteBar = ({ v }) => (
  <div data-testid="mobile-vote-bar" style={{ position: "fixed", left: 0, right: 0, bottom: "var(--mobile-bar, 0px)", zIndex: 30, background: "#fff", borderTop: "1px solid var(--border)",
    boxShadow: "0 -6px 20px rgba(20,35,42,.08)", padding: "12px 16px calc(16px + var(--panel-safe, env(safe-area-inset-bottom)))", display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p className="head" style={{ margin: 0, fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{v.pct}%</p>
      <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--muted)" }}>like this · {n(v.likes)} likes</p>
    </div>
    <VoteButtons v={v} size={48} compactLabels prefix="mobile-vote" />
  </div>
);
